import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faSpinner, faTimes } from '@/icons';
import { useTheme, createThemedStyles } from '../../contexts/ThemeContext';
import { ExpenseGroup } from '../../components/ExpenseGroup';
import { ExpenseGroupModel, ExpenseModel } from '../../types/case';
import { clientExpenseApi } from '../../api/clientExpenseApi';
import { useVisibilityAwarePolling } from '../../hooks/useVisibilityAwarePolling';
import { debugError } from '../../utils/debug';
import { ensureFinancialLineId } from '../../utils/financialLineId';
import { FullPageSpinner } from '../../components/FullPageSpinner';
import { Modal } from '../../components/Modal';

/** Shown when the link was superseded, timed out, or submitted—direct users to the newest email from their attorney. */
const EXPIRED_OR_INVALID_LINK_MESSAGE =
    'This link is no longer valid. Please check your email for the latest expense link you received from your attorney and use that instead.';

function userFacingTokenErrorMessage(apiMessage: string | undefined): string {
    if (!apiMessage) {
        return EXPIRED_OR_INVALID_LINK_MESSAGE;
    }
    const m = apiMessage.toLowerCase();
    if (
        m.includes('expired') ||
        m.includes('no longer valid') ||
        m.includes('invalid or expired') ||
        (m.includes('invalid') && m.includes('token'))
    ) {
        return EXPIRED_OR_INVALID_LINK_MESSAGE;
    }
    return apiMessage;
}

/**
 * Public client expense editing page (no authentication required)
 */
export const ClientExpensePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const { token: pathToken } = useParams<{ token?: string }>();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);

    /** Query string wins when both ?token= and path segment are present (matches API). */
    const token = searchParams.get('token') ?? pathToken ?? null;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [caseNumber, setCaseNumber] = useState<string | null>(null);
    const [partyAName, setPartyAName] = useState<string | null>(null);
    const [partyBName, setPartyBName] = useState<string | null>(null);
    const [expenseGroups, setExpenseGroups] = useState<ExpenseGroupModel[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSavedModalOpen, setIsSavedModalOpen] = useState(false);
    const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    // ConcurrentCaseEditGuard A2: while the client actively has a valid, unsubmitted portal open,
    // heartbeat the case's client-presence lease so the attorney editing the same case in the
    // wizard sees a live "a client is entering expenses" indicator. Visibility-gated (pauses when
    // the tab is hidden) and fire-and-forget.
    const clientPresenceActive = !!token && isTokenValid && !isSubmitted;
    useEffect(() => {
        if (clientPresenceActive && token) {
            clientExpenseApi.heartbeat(token); // immediate, so the indicator appears promptly
        }
    }, [clientPresenceActive, token]);
    useVisibilityAwarePolling(
        () => { if (token) clientExpenseApi.heartbeat(token); },
        15000,
        { enabled: clientPresenceActive, runOnVisible: true }
    );

    // Load expenses on mount
    useEffect(() => {
        const loadExpenses = async () => {
            if (!token) {
                setError('Invalid link. Please check the link and try again.');
                setLoading(false);
                return;
            }

            try {
                // Validate token first
                const validationResult = await clientExpenseApi.validateToken(token);
                if (!validationResult.success) {
                    setError(userFacingTokenErrorMessage(validationResult.message));
                    setLoading(false);
                    return;
                }

                setIsTokenValid(true);

                // Get expenses
                const expensesResult = await clientExpenseApi.getExpenses(token);
                if (!expensesResult.success) {
                    setError(userFacingTokenErrorMessage(expensesResult.message));
                    setLoading(false);
                    return;
                }
                if (!expensesResult.expenseGroups) {
                    setError(expensesResult.message || 'Failed to load expenses.');
                    setLoading(false);
                    return;
                }

                setCaseNumber(expensesResult.caseNumber || null);
                setPartyAName(expensesResult.partyAName || null);
                setPartyBName(expensesResult.partyBName || null);

                const processedGroups = expensesResult.expenseGroups.map((group) => ({
                    ...group,
                    expenses: (group.expenses || []).map((exp) => ({
                        ...exp,
                        lineId: ensureFinancialLineId(exp.lineId),
                    }))
                }));

                setExpenseGroups(processedGroups);
                setLoading(false);
            } catch (err: any) {
                debugError('Error loading expenses:', err);
                setError(err?.message || 'An error occurred while loading expenses. Please try again.');
                setLoading(false);
            }
        };

        loadExpenses();
    }, [token]);

    const handleUpdateExpenseGroup = (groupIndex: number, expenses: ExpenseModel[]) => {
        const updatedGroups = [...expenseGroups];
        updatedGroups[groupIndex] = {
            ...updatedGroups[groupIndex],
            expenses: expenses
        };
        setExpenseGroups(updatedGroups);
    };

    const grandTotal = expenseGroups.reduce(
        (sum, group) => sum + (group.expenses || []).reduce((s, exp) => s + (exp.amount ?? 0), 0),
        0
    );
    const formatCurrency = (amount: number) =>
        `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const handleSave = async (submit: boolean = false) => {
        if (!token) {
            setError('Invalid link. Please check the link and try again.');
            return;
        }

        if (submit) {
            setIsSubmitting(true);
        } else {
            setIsSaving(true);
        }
        setError(null);

        try {
            const result = await clientExpenseApi.saveExpenses(token, expenseGroups, submit);
            if (result.success) {
                if (submit) {
                    setIsSubmitted(true);
                } else {
                    setIsSavedModalOpen(true);
                }
            } else {
                setError(userFacingTokenErrorMessage(result.message));
            }
        } catch (err: any) {
            debugError('Error saving expenses:', err);
            setError(err?.message || 'An error occurred while saving expenses. Please try again.');
        } finally {
            setIsSaving(false);
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <FullPageSpinner message="Loading expenses..." />;
    }

    if (isSubmitted) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.background,
                padding: theme.spacing.lg,
            }}>
                <div style={{
                    maxWidth: '700px',
                    width: '100%',
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius.md,
                    padding: theme.spacing.xl,
                    boxShadow: theme.shadows?.md || '0 4px 6px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center',
                }}>
                    <h1 style={{
                        ...themedStyles.text.heading,
                        color: theme.colors.text.primary,
                        marginBottom: theme.spacing.md,
                    }}>
                        Thank you
                    </h1>
                    <p style={{
                        ...themedStyles.text.body,
                        color: theme.colors.text.primary,
                        marginBottom: 0,
                        lineHeight: 1.6,
                    }}>
                        Thank you for submitting your expenses. Your attorney has been notified.
                    </p>
                </div>
            </div>
        );
    }

    if (error && !isTokenValid) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.background,
                padding: theme.spacing.lg,
            }}>
                <div style={{
                    maxWidth: '600px',
                    width: '100%',
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius.md,
                    padding: theme.spacing.xl,
                    boxShadow: theme.shadows?.md || '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}>
                    <h1 style={{
                        ...themedStyles.text.heading,
                        color: theme.colors.status.error || '#ef4444',
                        marginBottom: theme.spacing.md,
                    }}>
                        Unable to Load Expenses
                    </h1>
                    <p style={{
                        ...themedStyles.text.body,
                        color: theme.colors.text.primary,
                        marginBottom: theme.spacing.lg,
                    }}>
                        {error}
                    </p>
                    <p style={{
                        ...themedStyles.text.body,
                        color: theme.colors.text.secondary,
                        fontSize: theme.typography.fontSize.sm,
                    }}>
                        If you believe this is an error, please contact your attorney.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.lg,
        }}>
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                paddingBottom: '56px',
            }}>
                {/* Header */}
                <div style={{
                    marginBottom: theme.spacing.xl,
                }}>
                    <h1 style={{
                        ...themedStyles.text.heading,
                        color: theme.colors.text.primary,
                        marginBottom: theme.spacing.sm,
                    }}>
                        Expense Information
                    </h1>
                    {caseNumber && (
                        <p style={{
                            ...themedStyles.text.body,
                            color: theme.colors.text.secondary,
                            fontSize: theme.typography.fontSize.sm,
                        }}>
                            Case {caseNumber}
                            {partyAName && partyBName ? ` • ${partyAName} and ${partyBName}` : null}
                        </p>
                    )}
                    <p style={{
                        ...themedStyles.text.body,
                        color: theme.colors.text.secondary,
                        fontSize: theme.typography.fontSize.md,
                        marginTop: theme.spacing.md,
                    }}>
                        Please fill out your expense information below. You can save your progress and return later, or submit when you're ready.
                    </p>

                    {/* Grand total - top (hidden, retained for alignment reference) */}
                    <div style={{
                        display: 'none',
                        marginTop: theme.spacing.lg,
                        padding: theme.spacing.md,
                        backgroundColor: theme.colors.surface,
                        borderRadius: theme.borderRadius.sm,
                        border: `1px solid ${theme.colors.border.default}`,
                        gridTemplateColumns: '1fr 180px auto',
                        gap: theme.spacing.md,
                        alignItems: 'center',
                        ...themedStyles.text.body,
                        fontWeight: 600,
                        color: theme.colors.text.primary,
                    }}>
                        <span>Grand total</span>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            width: '100%',
                            paddingRight: '160px',
                        }}>
                            <span style={{ textAlign: 'right' }}>{formatCurrency(grandTotal)}</span>
                        </div>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            style={{ fontSize: theme.typography.fontSize.md, visibility: 'hidden' }}
                            aria-hidden
                        />
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div style={{
                        padding: theme.spacing.md,
                        backgroundColor: '#fee2e2',
                        border: `1px solid ${theme.colors.status.error || '#ef4444'}`,
                        borderRadius: theme.borderRadius.sm,
                        marginBottom: theme.spacing.lg,
                    }}>
                        <p style={{
                            ...themedStyles.text.body,
                            color: '#991b1b',
                            margin: 0,
                        }}>
                            {error}
                        </p>
                    </div>
                )}

                {/* Expense Groups */}
                {expenseGroups.map((group, groupIndex) => (
                    <div key={groupIndex} style={{ marginBottom: theme.spacing.lg }}>
                        <ExpenseGroup
                            groupName={group.name || `Expense Group ${groupIndex + 1}`}
                            expenses={group.expenses || []}
                            groupIndex={groupIndex}
                            onUpdate={handleUpdateExpenseGroup}
                            isReadOnly={false}
                        />
                    </div>
                ))}

                {/* Grand total - bottom (hidden, retained for alignment reference) */}
                <div style={{
                    display: 'none',
                    marginBottom: theme.spacing.xl,
                    padding: theme.spacing.md,
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius.sm,
                    border: `1px solid ${theme.colors.border.default}`,
                    gridTemplateColumns: '1fr 180px auto',
                    gap: theme.spacing.md,
                    alignItems: 'center',
                    ...themedStyles.text.body,
                    fontWeight: 600,
                    color: theme.colors.text.primary,
                }}>
                    <span>Grand total</span>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        width: '100%',
                        paddingRight: '160px',
                    }}>
                        <span style={{ textAlign: 'right' }}>{formatCurrency(grandTotal)}</span>
                    </div>
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        style={{ fontSize: theme.typography.fontSize.md, visibility: 'hidden' }}
                        aria-hidden
                    />
                </div>

                {/* Floating footer - Grand total */}
                <div style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                }}>
                    <div style={{
                        width: '30%',
                        minWidth: 200,
                        backgroundColor: theme.colors.primary,
                        padding: theme.spacing.md,
                        textAlign: 'center',
                        borderTopLeftRadius: 20,
                        borderTopRightRadius: 20,
                        ...themedStyles.text.body,
                        fontWeight: 600,
                        color: '#fff',
                    }}>
                        Grand Total: {formatCurrency(grandTotal)}
                    </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: theme.spacing.md,
                    marginTop: theme.spacing.xl,
                    paddingTop: theme.spacing.xl,
                    borderTop: `1px solid ${theme.colors.border.default}`,
                }}>
                    <button
                        type="button"
                        onClick={() => handleSave(false)}
                        disabled={isSaving || isSubmitting}
                        style={{
                            ...themedStyles.button.secondary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.sm,
                            opacity: (isSaving || isSubmitting) ? 0.5 : 1,
                            cursor: (isSaving || isSubmitting) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {isSaving ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                Saving...
                            </>
                        ) : (
                            'Save for Later'
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsSubmitConfirmOpen(true)}
                        disabled={isSaving || isSubmitting}
                        style={{
                            ...themedStyles.button.primary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.sm,
                            opacity: (isSaving || isSubmitting) ? 0.5 : 1,
                            cursor: (isSaving || isSubmitting) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                Submitting...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faCheck} />
                                Submit
                            </>
                        )}
                    </button>
                </div>
            </div>

            <Modal
                isOpen={isSavedModalOpen}
                onClose={() => setIsSavedModalOpen(false)}
                title="Saved"
            >
                <div style={{ marginBottom: theme.spacing.lg }}>
                    <p style={{
                        ...themedStyles.text.body,
                        color: theme.colors.text.primary,
                        margin: 0,
                        lineHeight: 1.5,
                    }}>
                        Your changes have been saved. For your attorney to see your changes, you will need to click Submit.
                    </p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        type="button"
                        onClick={() => setIsSavedModalOpen(false)}
                        style={themedStyles.button.primary}
                    >
                        OK
                    </button>
                </div>
            </Modal>

            <Modal
                isOpen={isSubmitConfirmOpen}
                onClose={() => setIsSubmitConfirmOpen(false)}
                title="Submit expenses?"
                headerBackgroundColor={theme.colors.primary}
            >
                <div style={{ marginBottom: theme.spacing.lg }}>
                    <p style={{
                        ...themedStyles.text.body,
                        color: theme.colors.text.primary,
                        margin: 0,
                        lineHeight: 1.5,
                    }}>
                        You can not make any further changes after submitting. Once you submit, your attorney will be notified. Continue?
                    </p>
                </div>
                <div style={{
                    display: 'flex',
                    gap: theme.spacing.md,
                    justifyContent: 'flex-end',
                }}>
                    <button
                        type="button"
                        onClick={() => setIsSubmitConfirmOpen(false)}
                        disabled={isSaving || isSubmitting}
                        style={{
                            ...themedStyles.button.secondary,
                            backgroundColor: '#FFFFFF',
                            border: `1px solid ${theme.colors.border.default}`,
                            color: theme.colors.text.primary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.sm,
                            opacity: (isSaving || isSubmitting) ? 0.5 : 1,
                            cursor: (isSaving || isSubmitting) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (isSaving || isSubmitting) return;
                            setIsSubmitConfirmOpen(false);
                            handleSave(true);
                        }}
                        disabled={isSaving || isSubmitting}
                        style={{
                            ...themedStyles.button.primary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.sm,
                            opacity: (isSaving || isSubmitting) ? 0.5 : 1,
                            cursor: (isSaving || isSubmitting) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <FontAwesomeIcon icon={faCheck} />
                        Submit
                    </button>
                </div>
            </Modal>
        </div>
    );
};
